import { selfGuidedTourVerifyUserEmail } from 'constants/routes';
import { fromFetch } from 'rxjs/fetch';
import { switchMap, of, catchError, map } from 'rxjs';
import { camelCaseObjectKeys } from 'utils/camelCase';
import { getAntiForgeryToken } from '../services/antiForgeryTokenService';

/**
 * Verifies a user's email address by sending a POST request to the self-guided tour verification API.
 *
 * @param {string} specId - The unique identifier for the specification.
 * @param {string} email - The email address to be verified.
 * @returns {Observable<Object>} An observable that emits the response data or an error object.
 *
 * The response object structure:
 * - If successful: The parsed JSON response from the API.
 * - If an error occurs: An object with the properties:
 *   - `error` {boolean}: Indicates an error occurred.
 *   - `message` {string}: The error message.
 */
const verifyUserEmail$ = ({ specId, email }) =>
  fromFetch(selfGuidedTourVerifyUserEmail, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ specId, email }),
    headers: {
      'Content-Type': 'application/json',
      'RequestVerificationToken': getAntiForgeryToken(),
    },
  }).pipe(
    switchMap(async (response) => {
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return of({ error: true, message: 'Error fetching data' });
    }),
    map(camelCaseObjectKeys),
    catchError((error) => of({ error: true, message: error.message }))
  );

export default verifyUserEmail$;
